import {ChangeDetectionStrategy, Component, computed, DestroyRef, effect, Injectable, Input, signal} from "@angular/core";
import {TranslateModule} from "@ngx-translate/core";
import {FormControl, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatAnchor, MatButton} from "@angular/material/button";
import {AvtaleId, AvtaleModell, AvtaleRessursClient, Avtalestatus, BrukerId, KanAvtaleTermineresInfoModell, KanTermineresStatus, TerminerAvtaleResponsModell, TerminerAvtaleSpesifikasjon} from "../../../../../felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {Appkonfigurasjon} from "../../../../../felles-ng2/tjenester/Appkonfigurasjon";
import {Temporal} from "temporal-polyfill";
import {FormaterZonedDateTimePipe} from "../../../../../felles-ng2/pipes/FormaterZonedDateTimePipe";
import {OtpSkjemaKomponent} from "../../../../delt/komponenter/skjema/otp-felt/otp-felt";
import {lesZonedDateTimeSerialisertOpt, skrivZonedDateTimeSerialisert} from "../../../../../felles-ng2/støttefunksjoner/temporal";
import {MatIcon} from "@angular/material/icon";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {NgClass} from "@angular/common";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {i18n, Oversettelser} from "../../../../../felles-ng2/tjenester/i18n/Oversettelser";
import {asynkArbeidPågårSignal} from "../../../../../felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {Observable} from "rxjs";
import {ZonedDateTimeSomPlainDatePipe} from "../../../../../felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {FormaterPlainDatePipe} from "../../../../../felles-ng2/pipes/FormaterPlainDatePipe";
import {InlineMeldingKomponent} from "../../../../delt/komponenter/inline-melding/inline-melding";
import {BrukerkontekstAdministrasjon} from "felles-ng2/tjenester/BrukerkontekstAdministrasjon";
import {Filer} from "felles/tjenester/Filer";
import {uriEnkodet} from "felles/stoettefunk/uri";
import ZonedDateTime = Temporal.ZonedDateTime;
import {Dialog} from "felles-ng2/tjenester/Dialog";

@Component({
    selector: 'terminer-avtale',
    templateUrl: './terminer-avtale.html',
    styleUrls: ['./terminer-avtale.scss'],
    imports: [
        TranslateModule,
        FormsModule,
        MatAnchor,
        ReactiveFormsModule,
        FormaterZonedDateTimePipe,
        MatButton,
        OtpSkjemaKomponent,
        MatIcon,
        NgClass,
        ZonedDateTimeSomPlainDatePipe,
        FormaterPlainDatePipe,
        InlineMeldingKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TerminerAvtaleKomponent {
    static ajsSelector = 'terminerAvtale';


    avtaleSomSignal =signal<AvtaleModell|null>(null);
    otpKodeSkjema = new FormControl('', {validators: Validators.required});
    otpValideringFeilet = signal(false);
    zipNedlastetTidspunkt = computed<ZonedDateTime | null>(() => {
        if (erNil(this.avtaleSomSignal())) return null;
        return lesZonedDateTimeSerialisertOpt(this.avtaleSomSignal()!.nedlastningsTidspunkt);}
    );

    kanAvtaleAvsluttes = computed(()=>{
        if (!this.kanBrukerTerminereAvtale()) return false;
        if (this.kanAvtaleTermineresInfo().status !== KanTermineresStatus.OK) return false;
        // hvis status er OK så er nedlastingstidspunkt satt
        return true;
    } );

    lagrer = asynkArbeidPågårSignal();

    kanBrukerTerminereAvtale = computed(()=> {
        if (erNil(this.avtaleSomSignal())) return false;
         return this.avhengigheter.autentisertBrukersId() === this.avtaleSomSignal()!.kontaktperson.id
    });

    // kan ike sjekke mot Avtalestatus.prøveperiode i html pga norske bokstaver i statusnavn.
    avtaleIProvePeriode = computed(() => {
        if (erNil(this.avtaleSomSignal())) {
            return false;
        }
        return this.avtaleSomSignal()!.avtalestatus === Avtalestatus.prøveperiode;
    });

    erProvePeriodeUtloept = computed(() => {
        if (erNil(this.avtaleSomSignal())) {
            return false;
        }
        return this.avtaleSomSignal()!.avtalestatus === Avtalestatus.prøveperiodeUtløpt;
    });

    kanAvtaleTermineresInfo = signal<KanAvtaleTermineresInfoModell>({status:KanTermineresStatus.IKKE_LASTET_NED,antallDagerFoerSletting:0,beregnetSlettedato:skrivZonedDateTimeSerialisert(Temporal.Now.zonedDateTimeISO()), nedlastningMaaVaereNyereEnn:skrivZonedDateTimeSerialisert(Temporal.Now.zonedDateTimeISO()), maaAvsluttesFoer:null})


    //ikke def som input() da dette ikke fungerer når komponent kalles fra ajs
    @Input({required: true}) set avtale(avtale: AvtaleModell) {
        this.avtaleSomSignal.set(avtale);
    }

    constructor(private destroyRef: DestroyRef, private avhengigheter: TerminerAvtaleKomponentAvhengigheter) {
        effect(() => {
            if (this.kanAvtaleAvsluttes()){
                this.otpKodeSkjema.enable();
            } else {
                this.otpKodeSkjema.disable();
            }
        });

        effect(() => {
            if (erNil(this.avtaleSomSignal())) return;

            this.avhengigheter.kanAvtaleTermineres(this.avtaleSomSignal()!.id).then(kanAvtaleTerminersInfo=>{
                this.kanAvtaleTermineresInfo.set(kanAvtaleTerminersInfo)
            }  );
        });
    }

    lastNedKlikket(){
        this.avhengigheter.bekreftNedlasningDialog();
        // vent halvt sekund - last avtale på nytt - da er nedlastningsTidspunkt satt
        setTimeout(()=>{
            this.avhengigheter.hentAvtale(this.avtaleSomSignal()!.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(a=>{
                this.avtaleSomSignal.set(a);
            });
            this.avhengigheter.kanAvtaleTermineres(this.avtaleSomSignal()!.id).then(kanAvtaleTerminersInfo=> this.kanAvtaleTermineresInfo.set(kanAvtaleTerminersInfo) );
        }, 500);
    }

    url() {
        return this.avhengigheter.serviceRotUrl() + "/api/avtale/" + this.avtaleSomSignal()!.id + "/datadump/" + this.filnavn();
    }

    filnavn() {
        return uriEnkodet(Filer.somSikkertFilnavn(this.avtaleSomSignal()!.navn)) + ".zip";
    }

    async terminer(){
        await this.lagrer.utfør(() => {
            return this.avhengigheter.terminer(this.avtaleSomSignal()!.id, {verifiseringskode: this.otpKodeSkjema.value!})
        })
          .then(responseModell => {
              if (responseModell.feilKode) {
                  this.otpValideringFeilet.set(true);
              } else {
                  this.otpValideringFeilet.set(false);
              }
              this.avtaleSomSignal.set(responseModell.avtale);
              if (responseModell.terminert) {
                  this.avhengigheter.relastKontekst();
              }
          })
    }

    protected readonly KanTermineresStatus = KanTermineresStatus;
    protected readonly Avtalestatus = Avtalestatus;
    protected readonly erNil = erNil;
}

@Injectable()
export class TerminerAvtaleKomponentAvhengigheter {

    constructor(private konfigurasjon: Appkonfigurasjon, private avtaleRessurs: AvtaleRessursClient, private kontekstTilbyder: BrukerkontekstTilbyder, private brukerkontekstAdministrasjon: BrukerkontekstAdministrasjon, private dialog:Dialog, private oversettelser: Oversettelser) {
    }

    serviceRotUrl() {
        return this.konfigurasjon.serviceRotUrl;
    }

    terminer(avtaleId: AvtaleId, spesifikasjon: TerminerAvtaleSpesifikasjon): Promise<TerminerAvtaleResponsModell> {
        return this.avtaleRessurs.terminerAvtale(avtaleId, spesifikasjon).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke avslutte avtale'));
    }

    kanAvtaleTermineres(avtaleId: AvtaleId): Promise<KanAvtaleTermineresInfoModell> {
        return this.avtaleRessurs.kanAvtaleTermineres(avtaleId).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke hente avtale'));
    }

    hentAvtale(avtaleId: AvtaleId): Observable<AvtaleModell> {
        return this.avtaleRessurs.hentEnkel(avtaleId).somObservable();
    }


    autentisertBrukersId(): BrukerId {
        return this.kontekstTilbyder.autentisertBruker().bruker.id;
    }

    relastKontekst(){
        this.brukerkontekstAdministrasjon.relastKontekst();
    }


    bekreftNedlasningDialog() {
        this.dialog.advarsel({tittel: this.oversettelser.oversett(i18n('Nedlastning startet')), innhold: this.oversettelser.oversett(i18n('Nedlastning har startet, den tar som regel noen minutter. Se i nettleserens nedlastninger.')), droppAvbrytKnapp: true});
    }


}
